"use client";

import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight, ChevronsLeft } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  isKeyboardDisabled?: boolean;
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollToStart: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  canScrollToStart: boolean;
  scrollSnaps: number[];
  selectedIndex: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  isKeyboardDisabled = false,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [canScrollToStart, setCanScrollToStart] = React.useState(false);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const onInit = React.useCallback((carouselApi: CarouselApi) => {
    if (!carouselApi) {
      return;
    }
    setScrollSnaps(carouselApi.scrollSnapList());
  }, []);

  const onSelect = React.useCallback((carouselApi: CarouselApi) => {
    if (!carouselApi) {
      return;
    }
    setCanScrollPrev(carouselApi.canScrollPrev());
    setCanScrollNext(carouselApi.canScrollNext());
    setCanScrollToStart(carouselApi.selectedScrollSnap() > 0);
    setSelectedIndex(carouselApi.selectedScrollSnap());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const scrollToStart = React.useCallback(() => {
    api?.scrollTo(0);
  }, [api]);

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isKeyboardDisabled) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext, isKeyboardDisabled],
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }
    onInit(api);
    onSelect(api);
    api.on("reInit", onInit);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("select", onSelect);
      api?.off("reInit", onInit);
      api?.off("reInit", onSelect);
    };
  }, [api, onInit, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        scrollToStart,
        scrollTo,
        canScrollPrev,
        canScrollNext,
        canScrollToStart,
        scrollSnaps,
        selectedIndex,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
      <div
        className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full transition-opacity duration-300",
        orientation === "horizontal"
          ? "-left-4 top-1/2 -translate-y-1/2"
          : "left-1/2 top-4 -translate-x-1/2 rotate-90",
        !canScrollPrev && "pointer-events-none !opacity-0",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full transition-opacity duration-300",
        orientation === "horizontal"
          ? "-right-4 top-1/2 -translate-y-1/2"
          : "bottom-4 left-1/2 -translate-x-1/2 rotate-90",
        !canScrollNext && "pointer-events-none !opacity-0",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

function CarouselToStart({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollToStart, canScrollToStart, api } = useCarousel();
  const [isVisibleAfterMultipleScrolls, setIsVisibleAfterMultipleScrolls] = React.useState(false);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const checkScrollPosition = () => {
      if (api.selectedScrollSnap() > 1) {
        setIsVisibleAfterMultipleScrolls(true);
      } else {
        setIsVisibleAfterMultipleScrolls(false);
      }
    };

    checkScrollPosition();
    api.on("select", checkScrollPosition);

    return () => {
      api.off("select", checkScrollPosition);
    };
  }, [api]);

  return (
    <Button
      data-slot="carousel-to-start"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full transition-opacity duration-300",
        orientation === "horizontal"
          ? "left-4 top-1/2 -translate-y-1/2"
          : "left-1/2 top-12 -translate-x-1/2 rotate-90",
        (!canScrollToStart || !isVisibleAfterMultipleScrolls) && "pointer-events-none !opacity-0",
        className,
      )}
      disabled={!canScrollToStart}
      onClick={scrollToStart}
      {...props}
    >
      <ChevronsLeft />
      <span className="sr-only">Back to start</span>
    </Button>
  );
}

interface CarouselDotButtonsProps extends React.ComponentProps<"div"> {
  dotClassName?: string;
  activeDotClassName?: string;
}

function CarouselDotButtons({
  className,
  dotClassName,
  activeDotClassName,
  ...props
}: CarouselDotButtonsProps) {
  const { scrollSnaps, selectedIndex, scrollTo } = useCarousel();

  if (scrollSnaps.length <= 1) {
    return null;
  }

  return (
    <div
      className={cn("flex items-center justify-center gap-1.5", className)}
      data-slot="carousel-dots"
      {...props}
    >
      {scrollSnaps.map((_, index) => (
        <button
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          type="button"
          onClick={() => scrollTo(index)}
          className={cn(
            "h-2 w-2 rounded-full bg-gray-300 transition-all duration-200",
            "hover:bg-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            index === selectedIndex && "bg-gray-600",
            dotClassName,
            index === selectedIndex && activeDotClassName,
          )}
          aria-label={`Go to slide ${index + 1}`}
          aria-current={index === selectedIndex ? "true" : undefined}
        />
      ))}
    </div>
  );
}

interface CarouselSlideControlsProps extends React.ComponentProps<"div"> {
  showDots?: boolean;
  showArrows?: boolean;
}

function CarouselSlideControls({
  className,
  showDots = true,
  showArrows = true,
  children,
  ...props
}: CarouselSlideControlsProps) {
  return (
    <div
      className={cn("flex items-center justify-center gap-2", className)}
      data-slot="carousel-controls"
      {...props}
    >
      {showArrows && <CarouselPrevious className="static translate-x-0 translate-y-0" />}
      {showDots && <CarouselDotButtons />}
      {showArrows && <CarouselNext className="static translate-x-0 translate-y-0" />}
      {children}
    </div>
  );
}

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselToStart,
  CarouselDotButtons,
  CarouselSlideControls,
  type CarouselApi,
};
